import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Avatar,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from "@mui/material";
import Grid from "./utils/CompatGrid";
import UploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getCategories,
  getProduct,
  createProduct,
  updateProduct,
} from "./api/labelStudioApi";
import { BARCODE_TYPES } from "./utils/labelSizes";
import BarcodeCanvas from "./components/BarcodeCanvas";
import QrCodePreview from "./components/QrCodePreview";

const WEIGHT_UNITS = ["g", "kg", "ml", "L", "pcs"];

const emptyProduct = {
  categoryId: "",
  productNameEn: "",
  productNameMr: "",
  brandNameEn: "",
  brandNameMr: "",
  descriptionEn: "",
  descriptionMr: "",
  productImageUrl: "",
  brandLogoUrl: "",
  vegType: "VEG",
  countryOfOrigin: "India",
  mrp: "",
  sellingPrice: "",
  discount: "",
  gst: "",
  hsnCode: "",
  netWeight: "",
  weightUnit: "g",
  pieces: "",
  volume: "",
  ingredientsEn: "",
  ingredientsMr: "",
  manufacturerName: "",
  manufacturerAddress: "",
  manufacturerEmail: "",
  manufacturerPhone: "",
  manufacturerWebsite: "",
  manufacturingDate: "",
  packedDate: "",
  expiryDate: "",
  bestBefore: "",
  fssaiNumber: "",
  storageInstructionsEn: "",
  storageInstructionsMr: "",
  barcodeType: "CODE128",
  barcodeValue: "",
  sku: "",
  nutrition: {
    energy: "",
    protein: "",
    fat: "",
    carbohydrates: "",
    sugar: "",
    fibre: "",
    sodium: "",
    iron: "",
    calcium: "",
  },
  customNutrition: [],
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
const LAST_TAB_INDEX = 7;
const getApiErrorMessage = (err) => {
  const trace = err?.response?.data?.trace || "";
  if (trace.includes("brand_logo_url")) {
    return "Brand Logo could not be saved. Restart the backend so the image column migration can run, then try again.";
  }
  if (trace.includes("product_image_url")) {
    return "Product Image could not be saved. Restart the backend so the image column migration can run, then try again.";
  }
  return err?.response?.data?.message || "Failed to save product";
};

const ProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getProduct(id).then((p) => {
      let customNutrition = [];
      try {
        customNutrition = p.nutrition?.customFieldsJson
          ? JSON.parse(p.nutrition.customFieldsJson)
          : [];
      } catch (e) {
        customNutrition = [];
      }
      setForm({
        ...emptyProduct,
        ...p,
        categoryId: p.category?.id || "",
        nutrition: { ...emptyProduct.nutrition, ...(p.nutrition || {}) },
        customNutrition,
      });
    });
  }, [id, isEdit]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateNutrition = (field, value) =>
    setForm((f) => ({ ...f, nutrition: { ...f.nutrition, [field]: value } }));

  const addCustomNutrition = () =>
    setForm((f) => ({
      ...f,
      customNutrition: [
        ...f.customNutrition,
        { label: "", labelMr: "", value: "", unit: "" },
      ],
    }));

  const updateCustomNutrition = (idx, field, value) =>
    setForm((f) => ({
      ...f,
      customNutrition: f.customNutrition.map((row, i) =>
        i === idx ? { ...row, [field]: value } : row,
      ),
    }));

  const removeCustomNutrition = (idx) =>
    setForm((f) => ({
      ...f,
      customNutrition: f.customNutrition.filter((_, i) => i !== idx),
    }));

  const handleImageUpload = async (field, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    update(field, dataUrl);
    event.target.value = "";
  };

  const handleSave = async () => {
    if (!form.productNameEn.trim()) {
      toast.error("Product Name (English) is required");
      setTab(0);
      return;
    }
    // Save progresses through the tabs; the product is only persisted on the last tab.
    if (tab < LAST_TAB_INDEX) {
      setTab(tab + 1);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        productImageUrl: form.productImageUrl?.trim?.() || form.productImageUrl,
        brandLogoUrl: form.brandLogoUrl?.trim?.() || form.brandLogoUrl,
        category: form.categoryId ? { id: form.categoryId } : null,
        mrp: form.mrp === "" ? null : Number(form.mrp),
        sellingPrice:
          form.sellingPrice === "" ? null : Number(form.sellingPrice),
        discount: form.discount === "" ? null : Number(form.discount),
        gst: form.gst === "" ? null : Number(form.gst),
        netWeight: form.netWeight === "" ? null : Number(form.netWeight),
        pieces: form.pieces === "" ? null : Number(form.pieces),
        nutrition: {
          energy:
            form.nutrition.energy === "" ? null : Number(form.nutrition.energy),
          protein:
            form.nutrition.protein === ""
              ? null
              : Number(form.nutrition.protein),
          fat: form.nutrition.fat === "" ? null : Number(form.nutrition.fat),
          carbohydrates:
            form.nutrition.carbohydrates === ""
              ? null
              : Number(form.nutrition.carbohydrates),
          sugar:
            form.nutrition.sugar === "" ? null : Number(form.nutrition.sugar),
          fibre:
            form.nutrition.fibre === "" ? null : Number(form.nutrition.fibre),
          sodium:
            form.nutrition.sodium === "" ? null : Number(form.nutrition.sodium),
          iron: form.nutrition.iron === "" ? null : Number(form.nutrition.iron),
          calcium:
            form.nutrition.calcium === ""
              ? null
              : Number(form.nutrition.calcium),
          customFieldsJson: JSON.stringify(form.customNutrition || []),
        },
      };
      delete payload.categoryId;
      delete payload.customNutrition;

      if (isEdit) {
        await updateProduct(id, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product created");
      }
      navigate("/label-studio/products");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {isEdit ? "Edit Product" : "Add Product"}
      </Typography>

      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Basic Info" />
          <Tab label="Pricing" />
          <Tab label="Packaging" />
          <Tab label="Ingredients" />
          <Tab label="Nutrition" />
          <Tab label="Manufacturer" />
          <Tab label="Storage" />
          <Tab label="Barcode / QR" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Product Name (English)"
                  fullWidth
                  required
                  value={form.productNameEn}
                  onChange={(e) => update("productNameEn", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Product Name (Marathi)"
                  fullWidth
                  value={form.productNameMr}
                  onChange={(e) => update("productNameMr", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Brand Name (English)"
                  fullWidth
                  value={form.brandNameEn}
                  onChange={(e) => update("brandNameEn", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Brand Name (Marathi)"
                  fullWidth
                  value={form.brandNameMr}
                  onChange={(e) => update("brandNameMr", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Category"
                  fullWidth
                  value={form.categoryId}
                  onChange={(e) => update("categoryId", e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nameEn}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Country of Origin"
                  fullWidth
                  value={form.countryOfOrigin}
                  onChange={(e) => update("countryOfOrigin", e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description (English)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.descriptionEn}
                  onChange={(e) => update("descriptionEn", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description (Marathi)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.descriptionMr}
                  onChange={(e) => update("descriptionMr", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Product Image
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={form.productImageUrl}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box
                    sx={{ display: "flex", gap: 2, flex: 1, flexWrap: "wrap" }}
                  >
                    <TextField
                      label="Product Image URL"
                      fullWidth
                      placeholder="https://example.com/product-image.jpg"
                      value={form.productImageUrl}
                      onChange={(e) =>
                        update("productImageUrl", e.target.value)
                      }
                    />
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload("productImageUrl", e)
                        }
                      />
                    </Button>
                    
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Upload an image file or paste an image URL.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Brand Logo
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={form.brandLogoUrl}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box
                    sx={{ display: "flex", gap: 2, flex: 1, flexWrap: "wrap" }}
                  >
                    <TextField
                      label="Brand Logo URL"
                      fullWidth
                      placeholder="https://example.com/brand-logo.jpg"
                      value={form.brandLogoUrl}
                      onChange={(e) => update("brandLogoUrl", e.target.value)}
                    />
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleImageUpload("brandLogoUrl", e)}
                      />
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Upload a logo file or paste an image URL.
                </Typography>
              </Grid>
            </Grid>
       
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="MRP"
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">&#8377;</InputAdornment>
                    ),
                  }}
                  value={form.mrp}
                  onChange={(e) => update("mrp", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Selling Price"
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">&#8377;</InputAdornment>
                    ),
                  }}
                  value={form.sellingPrice}
                  onChange={(e) => update("sellingPrice", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Discount (%)"
                  type="number"
                  fullWidth
                  value={form.discount}
                  onChange={(e) => update("discount", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="GST (%)"
                  type="number"
                  fullWidth
                  value={form.gst}
                  onChange={(e) => update("gst", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="HSN Code"
                  fullWidth
                  value={form.hsnCode}
                  onChange={(e) => update("hsnCode", e.target.value)}
                />
              </Grid>
                   <Grid item xs={12} md={4}>
                <ToggleButtonGroup
                  exclusive
                  value={form.vegType}
                  onChange={(e, v) => v && update("vegType", v)}
                  size="small"
                >
                  <ToggleButton value="VEG" color="success">
                    Veg
                  </ToggleButton>
                  <ToggleButton value="NON_VEG" color="error">
                    Non-Veg
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Net Weight"
                  type="number"
                  fullWidth
                  value={form.netWeight}
                  onChange={(e) => update("netWeight", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Weight Unit"
                  fullWidth
                  value={form.weightUnit}
                  onChange={(e) => update("weightUnit", e.target.value)}
                >
                  {WEIGHT_UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Pieces"
                  type="number"
                  fullWidth
                  value={form.pieces}
                  onChange={(e) => update("pieces", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Volume"
                  fullWidth
                  value={form.volume}
                  onChange={(e) => update("volume", e.target.value)}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Ingredients (English)"
                  fullWidth
                  multiline
                  minRows={3}
                  value={form.ingredientsEn}
                  onChange={(e) => update("ingredientsEn", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Ingredients (Marathi)"
                  fullWidth
                  multiline
                  minRows={3}
                  value={form.ingredientsMr}
                  onChange={(e) => update("ingredientsMr", e.target.value)}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <Grid container spacing={2}>
              {[
                "energy",
                "protein",
                "fat",
                "carbohydrates",
                "sugar",
                "fibre",
                "sodium",
                "iron",
                "calcium",
              ].map((key) => (
                <Grid item xs={6} md={3} key={key}>
                  <TextField
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    type="number"
                    fullWidth
                    value={form.nutrition[key]}
                    onChange={(e) => updateNutrition(key, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Custom Nutrition Fields
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addCustomNutrition}
                >
                  Add Field
                </Button>
              </Box>
              {form.customNutrition.map((row, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Label (English)"
                      size="small"
                      fullWidth
                      value={row.label}
                      onChange={(e) =>
                        updateCustomNutrition(idx, "label", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Label (Marathi)"
                      size="small"
                      fullWidth
                      value={row.labelMr}
                      onChange={(e) =>
                        updateCustomNutrition(idx, "labelMr", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Value"
                      size="small"
                      fullWidth
                      value={row.value}
                      onChange={(e) =>
                        updateCustomNutrition(idx, "value", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Unit"
                      size="small"
                      fullWidth
                      value={row.unit}
                      onChange={(e) =>
                        updateCustomNutrition(idx, "unit", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton
                      color="error"
                      onClick={() => removeCustomNutrition(idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Manufacturer Name"
                  fullWidth
                  value={form.manufacturerName}
                  onChange={(e) => update("manufacturerName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="FSSAI Number"
                  fullWidth
                  value={form.fssaiNumber}
                  onChange={(e) => update("fssaiNumber", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.manufacturerAddress}
                  onChange={(e) =>
                    update("manufacturerAddress", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email"
                  fullWidth
                  value={form.manufacturerEmail}
                  onChange={(e) => update("manufacturerEmail", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={form.manufacturerPhone}
                  onChange={(e) => update("manufacturerPhone", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Website"
                  fullWidth
                  value={form.manufacturerWebsite}
                  onChange={(e) =>
                    update("manufacturerWebsite", e.target.value)
                  }
                />
              </Grid>
              
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Storage Instructions (English)"
                  fullWidth
                  multiline
                  minRows={3}
                  value={form.storageInstructionsEn}
                  onChange={(e) =>
                    update("storageInstructionsEn", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Storage Instructions (Marathi)"
                  fullWidth
                  multiline
                  minRows={3}
                  value={form.storageInstructionsMr}
                  onChange={(e) =>
                    update("storageInstructionsMr", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={7}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Barcode Type"
                  fullWidth
                  value={form.barcodeType}
                  onChange={(e) => update("barcodeType", e.target.value)}
                >
                  {BARCODE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Barcode Value"
                  fullWidth
                  value={form.barcodeValue}
                  onChange={(e) => update("barcodeValue", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="SKU"
                  fullWidth
                  value={form.sku}
                  onChange={(e) => update("sku", e.target.value)}
                />
              </Grid>
              {form.barcodeValue && (
                <Grid item xs={12} sx={{ display: "flex", gap: 4, mt: 1 }}>
                  <Box>
                    <Typography variant="caption">Barcode Preview</Typography>
                    <BarcodeCanvas
                      value={form.barcodeValue}
                      type={form.barcodeType}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" display="block">
                      QR Preview
                    </Typography>
                    <QrCodePreview value={form.barcodeValue} size={80} />
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button onClick={() => navigate("/label-studio/products")}>
          Cancel
        </Button>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {saving
            ? "Saving..."
            : tab < LAST_TAB_INDEX
            ? "Save & Next"
            : "Save Product"}
        </Button>
      </Box>
    </Box>
  );
};

export default ProductForm;
